import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, Target, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { daysUntil, visualFor, COUNTDOWN_PHASE_STYLES } from "@/lib/visionVisuals";

/** Compact Vision-Board countdown strip for the Dashboard.
 *  Shows the user's top 4 goal-type vision items with a live days-remaining chip
 *  so they see their vision + timeline every day they open the app. */
export default function VisionCountdownStrip() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/vision/board");
        const goals = (data.items || [])
          .filter((it) => it.type === "goal" && it.target_date)
          .sort((a, b) => (a.target_date > b.target_date ? 1 : -1))
          .slice(0, 4);
        setItems(goals);
      } catch { /* ignore */ } finally { setLoaded(true); }
    })();
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="mb-8" data-testid="vision-countdown-strip">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-nunito text-lg font-bold tracking-tight text-[#2D3748]">
          <Target className="h-5 w-5 text-[#FF7E67]" strokeWidth={2.5} />
          Your vision · countdown
        </h2>
        <button
          onClick={() => nav("/app/vision")}
          data-testid="vision-strip-view-all"
          className="inline-flex items-center gap-1 rounded-full border border-[#FFE8E3] bg-white px-4 py-1.5 font-nunito text-xs font-bold text-[#C2410C] hover:bg-[#FFF9F5]">
          View board <ArrowRight className="h-3 w-3" strokeWidth={3} />
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((it) => {
          const cd = daysUntil(it.target_date);
          const { image, gradient } = visualFor(it);
          const phase = cd ? COUNTDOWN_PHASE_STYLES[cd.phase] : null;
          return (
            <button
              key={it.id}
              onClick={() => nav("/app/vision")}
              data-testid={`vision-strip-card-${it.id}`}
              className="group relative flex h-40 items-end overflow-hidden rounded-2xl text-left shadow-warm transition-transform hover:-translate-y-0.5">
              {image
                ? <img src={image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                : <div className="absolute inset-0"
                       style={{ background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})` }} />
              }
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/45 to-transparent" />
              {cd && (
                <div className={`absolute right-2 top-2 flex items-center gap-1 rounded-xl px-2.5 py-1 backdrop-blur ${phase.pill}`}>
                  <Clock className="h-3 w-3" strokeWidth={2.5} />
                  <div className="leading-tight">
                    <div className="text-sm font-extrabold">
                      {cd.days > 0 ? cd.days : Math.abs(cd.days)}
                    </div>
                    <div className="text-[8px] font-bold uppercase tracking-widest">
                      {cd.phase === "today" ? "TODAY" : cd.phase === "past" ? "past" : cd.days === 1 ? "day" : "days"}
                    </div>
                  </div>
                </div>
              )}
              <div className="relative z-10 p-3 text-white">
                <p className="font-nunito text-base font-extrabold leading-tight drop-shadow line-clamp-3">
                  {it.content}
                </p>
                {it.target_date && (
                  <p className="mt-1 font-quicksand text-[10px] text-white/80">
                    Target · {new Date(`${it.target_date}T00:00`).toLocaleDateString(undefined,
                      { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}
